import { useEffect, useState } from 'react';

import type { ChatState } from './reducer';
import { TYPED_MESSAGE_KINDS } from './types';

/**
 * The pacing layer. The reducer has no timers; this hook is the only thing in
 * the chat that knows about time, and all it does is dispatch `advance` on a
 * schedule and say whether the typing indicator should be on screen.
 *
 * Ticket 01's timings: 450 ms of "typing" before a coach message, 300 ms
 * between messages. `prefers-reduced-motion` collapses both to zero and drops
 * the indicator, so the whole thread lands as fast as React can render it.
 */
export const TYPING_MS = 450;
export const GAP_MS = 300;

/** `prefers-reduced-motion`, tracked live so a change mid-module takes effect. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');

    setReduced(query.matches);

    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);

    query.addEventListener('change', onChange);

    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export type PacingOptions = {
  chat: ChatState;
  advance: () => void;
};

/** Returns true while the coach is "typing" the next message. */
export function usePacing({ chat, advance }: PacingOptions): boolean {
  const reduced = usePrefersReducedMotion();
  const [typing, setTyping] = useState(false);

  const { status, queue, thread } = chat;
  const next = queue[0];
  // A tick with an empty queue is the step boundary: no one is typing, we are
  // just waiting a beat before the next slice starts.
  const typed = next !== undefined && TYPED_MESSAGE_KINDS.has(next.kind);

  useEffect(() => {
    if (status !== 'playing') {
      setTyping(false);

      return;
    }

    const timers: Array<ReturnType<typeof setTimeout>> = [];
    // No pause before the very first message — the learner has just tapped
    // Start and is watching an empty thread.
    const gap = reduced || thread.length === 0 ? 0 : GAP_MS;

    timers.push(
      setTimeout(() => {
        if (typed && !reduced) {
          setTyping(true);
        }

        timers.push(
          setTimeout(
            () => {
              setTyping(false);
              advance();
            },
            typed && !reduced ? TYPING_MS : 0,
          ),
        );
      }, gap),
    );

    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }

      setTyping(false);
    };
  }, [status, queue, thread.length, typed, reduced, advance]);

  return typing;
}
